import React, { useState, useEffect } from "react";

import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { UiLink } from "components/common";
import CustomToolbar from "components/GridToolbar";
import AssociateTransactionModal from "components/Modal/AssociateTransactionModal";
import SolsDataGrid from "components/SolsticeComponents/Sols-data-grid";

import { Container, TableContainer } from "./styles";
import "./styles";
import { round } from "components/CommonFns";

import CommentIcon from "@mui/icons-material/Comment";
import CommentsModal from "../CommentsModal";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";

import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import * as leaseEventActions from "redux/actions/leaseEventActions";
import * as userActions from "redux/actions/userActions";
import {
  LeaseEvent
} from "../../../../shared/utils/constants";
import NotificationAlert from "components/Notification/NotificationAlert";
import { formatNumberWithCommas } from "shared/utils";

function CustomNoRowsOverlay() {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        minHeight: "280px",
        backgroundColor: "white",
        color: "#01151D",
        fontFamily: "Source Sans Pro",
        fontWeight: 400,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ fontSize: "20px", fontFamily: "Source Sans Pro", fontWeight: 400 }}>
        No results were found
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "16px", fontFamily: "Source Sans Pro", fontWeight: 400 }}>
        Try adjusting your search or filters.
      </Typography>
    </Stack>
  );
}

function CustomLoadingOverlay() {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{ minHeight: "280px", backgroundColor: "white" }}
    >
      <LinearProgress sx={{ width: "200px" }} />
      <Typography variant="body2" sx={{ fontSize: "16px", fontFamily: "Source Sans Pro", fontWeight: 400, color: "#01151D" }}>
        Loading data...
      </Typography>
    </Stack>
  );
}

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    margin: 0,
    maxWidth: 500,
    fontSize: "16px",
  },
  ["p"]: {
    margin: 0,
  },
}));

function getRentableArea(params) {
  var input = `${
    params.row.rentableBuildingArea
      ? round(params.row.rentableBuildingArea, 2)
      : 0
  }`;
  return `${formatNumberWithCommas(input)} ${params.row.uomNameShort}`;
}

function formatDate(params) {
  if (params.value) {
    const formattedDate = moment.utc(params.value).format("DD MMM YYYY");
    return formattedDate;
  } else return "";
}

const LeaseEventsTable = (props) => {
  const {
    leaseEvents,
    eventType,
    request,
    totalLeaseEvents,
    loading,
    hideColumns,
    hideToolbar,
    showCheckbox = true,
    onLeaseNameClicked,
    onPageChange,
    onSortModelChange,
    onRefresh = () => {},
    selectionModel,
    onRowSelectionModelChange,
    actionIcons,
    onDelete,
    columnVisibilityModel,
    columnVisibilityModelChange,
    columnOrderSettings,
    columnOrderSettingsChange,
    isAdmin,
    pageSize,
    parent,
  } = props;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(()=>{
      setPaginationModel({page:0, pageSize:pageSize});
    },[pageSize]);

  const [selectedComment, setSelectedComment] = useState({});
  const dispatch = useDispatch();
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const selectedNotification = useSelector(
    (state) => state.currentUser.selectedNotification
  );
  const selectedLeaseEvent = useSelector((state) => state.leaseEvents.details);
   const [notificationAlert, setNotificationAlert] = useState({
        message: "",
        severity: "success",
        title: "",
        open: false,
      });
  const [assocTxModalOpen, setAssocTxModalOpen] = useState(false);
  const [selectedAssocTxData, setSelectedAssocTxData] = useState({ propertyName: "", transactions: [] });

  // State for grid columns
  const [gridColumns, setGridColumns] = useState([]);

  // Function to sort columns based on order settings
  const sortColumnsByOrder = (columns, orderSettings) => {
    if (!orderSettings || orderSettings.length === 0) {
      return [...columns];
    }

    // Define pinned columns for lease events
    const pinnedLeft = ["eventId", "leaseName"];
    const pinnedRight = ["actions"];

    // Create a map of field names to order values
    const orderMap = {};
    orderSettings.forEach(setting => {
      orderMap[setting.column] = setting.order !== undefined ? setting.order : 999;
    });

    // Separate columns into pinned and non-pinned
    const leftPinnedColumns = columns.filter(col => pinnedLeft.includes(col.field));
    const rightPinnedColumns = columns.filter(col => pinnedRight.includes(col.field));
    const nonPinnedColumns = columns.filter(col => 
      !pinnedLeft.includes(col.field) && !pinnedRight.includes(col.field)
    );

    // Sort non-pinned columns by their order value
    const sortedNonPinned = nonPinnedColumns.sort((a, b) => {
      const orderA = orderMap[a.field] !== undefined ? orderMap[a.field] : 999;
      const orderB = orderMap[b.field] !== undefined ? orderMap[b.field] : 999;
      return orderA - orderB;
    });

    // Combine: left pinned + sorted non-pinned + right pinned
    return [...leftPinnedColumns, ...sortedNonPinned, ...rightPinnedColumns];
  };

  useEffect(() => {
    if (selectedNotification) {
      dispatch(
        leaseEventActions.getLeaseEvent(selectedNotification.typeEntityId)
      );
    }
  }, [selectedNotification]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (request) {
        const { filters } = request;
        if (
          selectedNotification &&
          selectedNotification.notificationCount !== totalLeaseEvents &&
          filters &&
          filters.length === 0 &&
          !loading
        ) {
          setNotificationAlert({
            message:
              "Lease events notice dates have been changed or it's been over 30 days since you were notified.",
            severity: "error",
            title: "Some lease events may not be visible",
            open: true,
          });
        } else {
          setNotificationAlert({
            message: "",
            severity: "",
            title: "",
            open: false,
          });
        }
      }
    }, 2000); // 2000 milliseconds = 2 seconds delay

    // Cleanup function to clear the timeout if the component unmounts or the dependencies change
    return () => clearTimeout(timeoutId);
  }, [selectedNotification, totalLeaseEvents, loading]);

  useEffect(() => {
    if (selectedNotification && selectedLeaseEvent && selectedNotification.type !== LeaseEvent) {
      setIsCommentsModalOpen(true);
      var leasedata = { leaseEventId: selectedNotification.typeEntityId };
      setSelectedComment(leasedata);
    }
  }, [selectedNotification, selectedLeaseEvent]);

  const renderLeaseName = (params) => {
    return (
      <span
        style={{
          color: '#09779E',
          textDecoration: 'underline',
          textDecorationColor: '#09779E',
          textUnderlineOffset: '2px',
          cursor: 'pointer',
          fontFamily: '"Source Sans Pro"',
          fontSize: '16px',
          fontWeight: 400,
        }}
        onClick={() => {
          onLeaseNameClicked(params.row.leaseEventId);
        }}
      >
        {params.row.leaseName}
      </span>
    );
  };

  const handleOpenAssocTxModal = (propertyName, transactions) => {
    setSelectedAssocTxData({ propertyName, transactions: transactions || [] });
    setAssocTxModalOpen(true);
  };

  const handleCloseAssocTxModal = () => {
    setAssocTxModalOpen(false);
  };

  const renderAssociatedTransactions = (params) => {
    const { transactions, propertyName, leaseName } = params.row;
    const displayName = propertyName || leaseName || "";

    if (!transactions || transactions.length === 0) {
      return (
        <span
          style={{
            fontFamily: '"Source Sans Pro"',
            fontSize: "16px",
            fontWeight: 400,
            color: "#56656B",
          }}
        >
          NA
        </span>
      );
    }

    const transactionNames = (transactions || [])
      .map((tx) => tx.transactionName || "")
      .filter((name) => name)
      .join(", ");

    return (
      <div
        style={{
          fontFamily: '"Source Sans Pro"',
          fontSize: "16px",
          fontWeight: 400,
          color: "#09779E",
          textDecoration: "underline",
          textDecorationColor: "#DFE5E8",
          textUnderlineOffset: "2px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          cursor: "pointer",
        }}
        onClick={() => handleOpenAssocTxModal(displayName, transactions)}
      >
        {transactionNames || "NA"}
      </div>
    );
  };

  const onEditComment = (data) => {
    setIsCommentsModalOpen(true);
    setSelectedComment(data);
  };

  const handleCommentSave = () => {
    // onRefresh();
  };

  const onAlertClose = () => {
    setNotificationAlert({
      message: "",
      severity: "",
      title: "",
      open: false,
    });
  };

  const renderDate = (params) => {
    if (!params.value) return "";
    return moment.utc(params.value).local().format("DD MMM YYYY");
  };

  const renderLastComment = (params) => {
    if (!params.row.lastComment || !params.row.lastCommentCreatedDate) {
      return "-";
    }

    const { lastComment, lastCommentCreatedDate } = params.row;
    const htmlComment = (
      <div
        className="html-comment"
        dangerouslySetInnerHTML={{ __html: lastComment }}
      />
    );

    return (
      <HtmlTooltip 
        title={truncateHtml(lastComment, 1000)}
        placement="bottom-start"
        followCursor={false}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
              },
            },
          ],
        }}
      >
        <div className="comment-cell">{htmlComment}</div>
      </HtmlTooltip>
    );
  };

  const truncateHtml = (lastComment, maxLength) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(lastComment, "text/html");
    let liCount = 0;

    // Function to recursively truncate
    function truncateNode(node, remainingLength) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.length > remainingLength) {
          node.textContent = node.textContent.slice(0, remainingLength) + "...";
          return 0;
        } else {
          return remainingLength - node.textContent.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.nodeName == "LI") liCount = liCount + 1;
        for (let child of Array.from(node.childNodes)) {
          remainingLength = truncateNode(child, remainingLength);
          if (remainingLength === 0 || liCount > 10) {
            // Remove all subsequent siblings
            while (child.nextSibling) {
              node.removeChild(child.nextSibling);
            }
            break;
          }
        }
        return remainingLength;
      }
      return remainingLength;
    }

    truncateNode(doc.body, maxLength);

    let bodyHTML = doc.body.innerHTML;
    const decodedHtml = bodyHTML.replace(/&lt;/g, "<").replace(/&gt;/g, ">");

    const htmlComment1 = (
      <div
        className="html-comment"
        dangerouslySetInnerHTML={{ __html: decodedHtml }}
      />
    );

    return htmlComment1;
  };

  /* eventId and leaseName are fixed as left-pinned columns (positions 1 and 2); order not changeable in column visibility modal */
  let defaultColumns = [
    {
      field: "eventId",
      headerName: "Event Id",
      headerClassName: "grid-app-header",
      minWidth: 100,
      width: 120,
      maxWidth: 120,
      flex: 0,
      hideable: false,
    },
    {
      field: "leaseName",
      headerName: "Lease Name",
      renderCell: renderLeaseName,
      headerClassName: "grid-app-header",
      minWidth: 200,
      width: 250,
      maxWidth: 250,
      flex: 0,
      hideable: false,
    },
    {
      field: "leaseEventId",
      headerName: "TME Event Id",
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
      hideable: isAdmin,
    },
    {
      field: "leaseCode",
      headerName: "Tenure Id",
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "propertyName",
      headerName: "Property Name",
      headerClassName: "grid-app-header",
      minWidth: 200,
      // hideable: false,
      flex: 1,
    },
    {
      field: "streetAddress1",
      headerName: "Property Address",
      headerClassName: "grid-app-header",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "businessUnit",
      headerName: "Business Unit",
      headerClassName: "grid-app-header",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "countryName",
      headerName: "Country",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "stateProvince",
      headerName: "State / Province",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "city",
      headerName: "City",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "rentableBuildingArea",
      headerName: "Building Area",
      renderCell: getRentableArea,
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "eventType",
      headerName: "Event Type",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "leaseEventStatusName",
      headerName: "Event Status",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "tenureType",
      headerName: "Tenure Type",
      headerClassName: "grid-app-header",
      minWidth: 100,
      hideable: false,
      flex: 1,
      renderCell: (params) =>
        params.value ? params.value : "-",
    },
    {
      field: "lastComment",
      headerName: "Last Comment",
      renderCell: renderLastComment,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "noticeDate",
      headerName: "Notice Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "actionDate",
      headerName: "Event Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "expireDate",
      headerName: "Lease End Date",
      headerClassName: "grid-app-header",
      renderCell: formatDate,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "snoozedUntil",
      headerName: "Snoozed Until",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "snoozedComment",
      headerName: "Snoozed Comment",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "leaseEventIgnoredDate",
      headerName: "Ignored Date",
      renderCell: formatDate,
      headerClassName: "grid-app-header",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "ignoreComment",
      headerName: "Ignore Comment",
      headerClassName: "grid-app-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "transactions",
      headerName: "Associated Transactions",
      headerClassName: "grid-app-header",
      renderCell: renderAssociatedTransactions,
      sortable: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 100,
      width: 150,
      maxWidth: 150,
      flex: 0,
      headerName: "Actions",
      headerClassName: "grid-app-header action-icons",
      getActions: (params) => {
        let actionCellItems = [];

        if (actionIcons?.includes("comment")) {
          actionCellItems.push(
            <GridActionsCellItem
              icon={
                <CustomTooltip title="Comments" arrow placement="bottom">
                  <CommentIcon />
                </CustomTooltip>
              }
              label="Edit Comments"
              onClick={() => onEditComment(params.row)}
            />
          );
        }

        if (actionIcons?.includes("delete")) {
          actionCellItems.push(
            <GridActionsCellItem
              icon={
                <CustomTooltip title="Delete" arrow placement="bottom">
                  <DeleteOutlineOutlinedIcon />
                </CustomTooltip>
              }
              label="Delete"
              onClick={() => onDelete(params.id)}
            />
          );
        }

        return actionCellItems;
      },
    },
  ];

  let columns = [];
  switch (eventType) {
    case "all":
    default:
      columns = defaultColumns.filter((column) => {
        return (
          !column.field.toLowerCase().includes("ignore") &&
          !column.field.toLowerCase().includes("snooze")
        );
      });
      break;
    case "snoozed":
      columns = defaultColumns.filter((column) => {
        return !column.field.toLowerCase().includes("ignore");
      });
      break;

    case "ignored":
      columns = defaultColumns.filter((column) => {
        return !column.field.toLowerCase().includes("snooze");
      });
      break;
  }

  if (hideColumns) {
    hideColumns.forEach((hc) => {
      columns = columns.filter((c) => c.field !== hc);
    });
  }

  if (!actionIcons || actionIcons.length === 0) {
    columns = columns.filter((c) => c.field !== "actionIcons");
  }

  // Effect to sort and set grid columns based on order settings
  useEffect(() => {
    let finalColumns = [...columns];
    if (hideColumns) {
      hideColumns.forEach((hc) => {
        finalColumns = finalColumns.filter((c) => c.field !== hc);
      });
    }
    
    // Sort columns based on order settings
    const sortedColumns = sortColumnsByOrder(finalColumns, columnOrderSettings);
    setGridColumns(sortedColumns);
  }, [columnOrderSettings, eventType, hideColumns]);

  // SolsDataGrid calls onPageChange(page, pageSize) with two args; parent expects { page, pageSize }
  const handlePageChange = (page, pageSize) => {
    setPaginationModel({ page, pageSize });
    onPageChange({ page, pageSize });
  };

  const renderTableComponents = () => {
    if (hideToolbar) return { Toolbar: () => null };
    return { Toolbar: CustomToolbar };
  };

  return (
    <>
      <NotificationAlert
        onClose={onAlertClose}
        message={notificationAlert.message}
        open={notificationAlert.open}
        title={notificationAlert.title}
        severity={notificationAlert.severity}
      ></NotificationAlert>
      {isCommentsModalOpen && (
        <CommentsModal
          open={isCommentsModalOpen}
          onClose={() => {
            setIsCommentsModalOpen(false);
            onRefresh();
            if (
              selectedNotification &&
              selectedNotification.type !== LeaseEvent
            ) {
              dispatch(userActions.resetSelectedNotificationComment());
            }
            
          }}
          onSave={handleCommentSave}
          id={selectedComment.leaseEventId}
          data={leaseEvents}
        />
      )}
      {/* <Container $autoHeight={parent === "snooze"}> */}
        {/* <TableContainer> */}
          <SolsDataGrid
            height={parent === "snooze" ? "auto" : "calc(100vh - 190px)"}
            autoHeight={parent === "snooze"}
            loading={loading}
            getRowId={(row) => row.leaseEventId}
            columns={gridColumns.length > 0 ? gridColumns : columns}
            rows={leaseEvents}
            rowCount={totalLeaseEvents}
            pageSizeOptions={[10, 20, 30, 40, 50]}
            paginationModel={paginationModel}
            pagination
            paginationMode="server"
            onPageChange={handlePageChange}
            sortingMode="server"
            onSortModelChange={onSortModelChange}
            checkboxSelection={showCheckbox}
            checkboxSelectionVisibleOnly
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={columnVisibilityModelChange}
            onColumnOrderChange={(settings) => {
              if (columnOrderSettingsChange) {
                columnOrderSettingsChange(settings, gridColumns.length > 0 ? gridColumns : columns);
              }
            }}
            pinnedColumns={{
              left: ["__check__", "eventId", "leaseName"],
              right: ["actions"],
            }}
            showCellVerticalBorder={false}
            disableColumnFilter
            disableDensitySelector
            disableColumnMenu
          />
        {/* </TableContainer> */}
      {/* </Container> */}
      <AssociateTransactionModal
        open={assocTxModalOpen}
        onClose={handleCloseAssocTxModal}
        propertyName={selectedAssocTxData.propertyName}
        transactions={selectedAssocTxData.transactions}
        from="lease-event"
      />
    </>
  );
};

export default LeaseEventsTable;
